const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export function passkeysEnabled(config: { adminPasskeys?: boolean; mandatoryPasskeys?: boolean }) {
  return config.adminPasskeys === true || config.mandatoryPasskeys === true;
}

export function shouldShowEmailCodeOption(mandatoryPasskeys = false) {
  return !mandatoryPasskeys;
}

export function passkeyDismissalStorageKey(deployment: string, email: string) {
  return `aidkit-passkey-enrollment-dismissed:${deployment}:${email.trim().toLowerCase()}`;
}

export function shouldPromptForPasskeyEnrollment({
  enabled,
  supported,
  passkeyCount,
  dismissedAt,
  mandatory = false,
  now = Date.now(),
}: {
  enabled: boolean;
  supported: boolean;
  passkeyCount: number;
  dismissedAt: string | null;
  // When true, skip both the browser-support check and the 30-day dismissal
  // snooze: enrollment is prompted on every login until one exists, with no
  // escape hatch. A device that can't complete registration still gets the
  // prompt so it can fail visibly with an actionable "contact an admin"
  // message, rather than silently skipping straight into the app.
  mandatory?: boolean;
  now?: number;
}) {
  if (!enabled || passkeyCount > 0) return false;
  if (mandatory) return true;
  if (!supported) return false;
  const dismissalTime = dismissedAt ? Number(dismissedAt) : NaN;
  return !Number.isFinite(dismissalTime) || now - dismissalTime >= THIRTY_DAYS_MS;
}

export function shouldRequestPasskeyMethodSelection(
  enabled: boolean,
  supported: boolean,
  authStrategy: 'session' | 'user',
) {
  return enabled && supported && authStrategy === 'user';
}

const MAX_LABEL_LENGTH = 100;

type BrowserEnvironment = {
  userAgent?: string;
  platform?: string;
  maxTouchPoints?: number;
};

function browserName(userAgent: string) {
  if (/Edg\/|EdgA\/|EdgiOS\//.test(userAgent)) return 'Edge';
  if (/OPR\/|Opera\//.test(userAgent)) return 'Opera';
  if (/SamsungBrowser\//.test(userAgent)) return 'Samsung Internet';
  if (/FxiOS\/|Firefox\//.test(userAgent)) return 'Firefox';
  if (/CriOS\/|Chrome\/|Chromium\//.test(userAgent)) return 'Chrome';
  if (/Version\/.*Safari\//.test(userAgent)) return 'Safari';
  return null;
}

function operatingSystemName({ userAgent = '', platform = '', maxTouchPoints = 0 }: BrowserEnvironment) {
  if (/Windows NT/.test(userAgent) || /^Win/.test(platform)) return 'Windows';
  if (/CrOS/.test(userAgent)) return 'ChromeOS';
  if (/Android/.test(userAgent)) return 'Android';
  if (/iPad/.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1)) return 'iPadOS';
  if (/iPhone|iPod/.test(userAgent)) return 'iOS';
  if (/Mac OS X/.test(userAgent) || /^Mac/.test(platform)) return 'macOS';
  if (/Linux/.test(userAgent) || /^Linux/.test(platform)) return 'Linux';
  return null;
}

function browserEnvironmentName(environment?: BrowserEnvironment) {
  if (!environment) return null;
  const browser = browserName(environment.userAgent || '');
  const operatingSystem = operatingSystemName(environment);
  return browser && operatingSystem ? `${browser} on ${operatingSystem}` : null;
}

function enrollmentDate(now: number, locale?: string) {
  // Our language codes use underscores (fa_AF), which Intl rejects as malformed.
  const tag = locale?.replace(/_/g, '-');
  try {
    return new Intl.DateTimeFormat(tag || undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      .format(new Date(now));
  } catch {
    return new Date(now).toISOString().slice(0, 10);
  }
}

/**
 * Name the environment where enrollment started. WebAuthn does not expose the
 * authenticator's friendly device name, and it may live on another or synced
 * device, so keep the fallback deliberately generic.
 */
export function generatePasskeyLabel({
  now = Date.now(),
  locale,
  fallbackName = 'Passkey',
  environment,
}: {
  now?: number;
  locale?: string;
  fallbackName?: string;
  environment?: BrowserEnvironment;
}) {
  return `${browserEnvironmentName(environment) || fallbackName} · ${enrollmentDate(now, locale)}`.slice(0, MAX_LABEL_LENGTH);
}

export type PasskeyErrorKind = 'cancelled' | 'expired' | 'disabled' | 'unsupported' | 'failed';

export function passkeyErrorKind(error: any): PasskeyErrorKind {
  if (error?.name === 'NotAllowedError' || error?.code === 'ERROR_CEREMONY_ABORTED') return 'cancelled';
  // @simplewebauthn/browser throws this exact plain Error (no distinguishing
  // name/code) when navigator.credentials.create()/.get() aren't available at all.
  if (error?.message === 'WebAuthn is not supported in this browser') return 'unsupported';
  return 'failed';
}

// Only distinguishes backend codes that are safe to reveal without leaking
// credential/account existence; anything else collapses into the generic bucket.
export function passkeyBackendErrorKind(code: string | undefined): PasskeyErrorKind {
  switch (code) {
    case 'ceremony_expired':
    case 'invalid_or_used_ceremony':
      return 'expired';
    case 'passkeys_not_enabled':
    case 'passkeys_unavailable':
      return 'disabled';
    default:
      return 'failed';
  }
}
